import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import ISection from '../../model/ISection';
import { useAvailableSections } from '../useAvailableSections';
import IFeature from '../../model/IFeature';
import ICategory from '../../model/ICategory';
import { FeatureCodePrefix } from '../../../../../common/enum/FeatureCodePrefix';
import { useAvailableFeatures } from '../queries/useAvailableFeatures';
import useCustomizeCarContext from '../useCustomizeCarContext';

export const useSelectedFeatures = () => {
  const intl = useIntl();
  const { sections, displayOffersSection } = useAvailableSections();
  const { features } = useAvailableFeatures();
  const { optimisticSelectedFeatures } = useCustomizeCarContext();

  const preinstalledFeatures = useMemo(
    (): IFeature[] => features.filter(feature => feature.code?.split(':')[0] === FeatureCodePrefix.preInstalled),
    [features],
  );

  const findSelectedFeatures = (featureCategory: ISection) => {
    const selectedFeatureCategory = { ...featureCategory };
    const selectedFeature = selectedFeatureCategory.featuresList?.features.filter((feature: IFeature) =>
      optimisticSelectedFeatures.includes(feature.code),
    );
    selectedFeatureCategory.featuresList.features = selectedFeature;

    return selectedFeature.length ? selectedFeatureCategory : null;
  };

  const getSelectedSections = (sections: ISection[], preinstalledFeatures: IFeature[]): ISection[] =>
    sections.reduce((selectedSections: ISection[], section: ISection) => {
      const foundCategories = section.categories;
      const isPreinstallCategoryCode =
        preinstalledFeatures?.length && section.code === preinstalledFeatures[0].parentCategoryCode;
      const features = isPreinstallCategoryCode ? preinstalledFeatures : [];

      if (foundCategories?.length) {
        const selectedCategories: ICategory[] = getSelectedCategories(foundCategories);

        return sections.length
          ? [
              ...selectedSections,
              {
                label: section.label,
                code: section.code,
                disclaimerIndicative: section.attributes.disclaimerindicative,
                categories: selectedCategories,
                featuresList: {
                  features,
                },
              },
            ]
          : selectedSections;
      }
      const selectedFeature = findSelectedFeatures(section);

      return selectedFeature ? [...selectedSections, selectedFeature] : selectedSections;
    }, []);

  const getSelectedCategories = (categories: ICategory[]): ICategory[] =>
    categories.reduce((selectedCategories: ICategory[], category: ICategory) => {
      const selectedFeature = findSelectedFeatures(category as ISection);

      return selectedFeature ? [...selectedCategories, selectedFeature] : selectedCategories;
    }, []);

  const selectedFeatures = useMemo(
    () => (sections.length ? getSelectedSections(sections, preinstalledFeatures) : []),
    [sections, preinstalledFeatures],
  );

  const selectedAllMiscFeatures = useMemo(
    (): IFeature[] =>
      features.filter(feature => {
        return (
          feature.code?.split(':')[0] === FeatureCodePrefix.miscOption &&
          optimisticSelectedFeatures.includes(feature.code)
        );
      }),
    [optimisticSelectedFeatures, features],
  );

  const miscFeatures = useMemo((): ISection[] => {
    const offersFeatures = displayOffersSection
      ? selectedAllMiscFeatures.filter(feature => feature.prices?.[0]?.money?.amount < 0)
      : [];

    const noOffersFeatures =
      offersFeatures.length > 0
        ? selectedAllMiscFeatures.filter(feature => !offersFeatures.some(offer => offer.code === feature.code))
        : selectedAllMiscFeatures;

    const miscOffers = {
      code: '_MISC_OFFERS',
      label: intl.formatMessage({
        id: 'trans__configurator__customise_page_summary_section__offers',
      }),
      featuresList: {
        features: offersFeatures,
      },
    };

    const miscNoOffers = {
      code: '_MISC',
      label: intl.formatMessage({
        id: 'trans__configurator__customise_page_summary_expanded__misc_title',
      }),
      featuresList: {
        features: noOffersFeatures,
      },
    };

    return [miscNoOffers, miscOffers];
  }, [displayOffersSection, selectedAllMiscFeatures]);

  return { selectedFeatures, selectedMiscFeatures: miscFeatures[0], selectedMiscOffers: miscFeatures[1] };
};
